// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Import Document Page (Sprint 5)
// Drag & drop file upload → parse → preview → confirm import
// ═══════════════════════════════════════════════════════════════

import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { C } from '../../../../../lib/design';
import { Ic, ICON_PATHS } from '../../../../../lib/ui';
import { AppHeader } from '../../../../shared/components/AppHeader';
import {
  parsearDocumento,
  confirmarImportacion,
  fetchProcesos,
} from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';

// ─── Types ──────────────────────────────────────────────────────────────────

type Step = 'upload' | 'preview' | 'confirming' | 'done' | 'error';

interface Bloque {
  tipo: string;
  contenido: any;
  orden: number;
}

interface Seccion {
  titulo: string;
  nivel: number;
  orden: number;
  bloques: Bloque[];
}

interface Metadatos {
  codigo?: string;
  nombre?: string;
  empresa?: string;
  tipoDocumental?: string;
  autor?: string;
  titulo?: string;
}

interface ParseResult {
  metadatos: Metadatos;
  secciones: Seccion[];
  advertencias: string[];
}

interface ProcesoOption {
  id: string;
  codigo: string;
  nombre: string;
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const card: React.CSSProperties = {
  backgroundColor: C.surface,
  borderRadius: 10,
  border: `1px solid ${C.border}`,
  overflow: 'hidden',
};

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 24px',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: 'inherit',
  color: '#fff',
  backgroundColor: C.azul2,
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'background 0.15s, opacity 0.15s',
};

const btnSecondary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 24px',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'inherit',
  color: C.textSec,
  backgroundColor: 'transparent',
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'background 0.15s',
};

const inputStyle: React.CSSProperties = {
  padding: '10px 14px',
  fontSize: 14,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  backgroundColor: C.bg,
  color: C.text,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: C.textSec,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 4,
};

// ─── Document type options ──────────────────────────────────────────────────

const TIPO_DOCUMENTAL_OPTIONS = [
  { value: 'PR', label: 'PR — Procedimiento' },
  { value: 'NC', label: 'NC — Norma Corporativa' },
  { value: 'IT', label: 'IT — Instruccion de Trabajo' },
  { value: 'MN', label: 'MN — Manual' },
  { value: 'PA', label: 'PA — Plan de Accion' },
  { value: 'RE', label: 'RE — Reporte' },
  { value: 'FC', label: 'FC — Ficha de Caracterizacion' },
  { value: 'FL', label: 'FL — Flujograma' },
  { value: 'HE', label: 'HE — Herramienta' },
  { value: 'MO', label: 'MO — Modelo' },
  { value: 'PT', label: 'PT — Protocolo' },
];

// ─── Component ──────────────────────────────────────────────────────────────

export function ImportDocumentPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [procesos, setProcesos] = useState<ProcesoOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());

  // Editable metadata
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [tipoDocumental, setTipoDocumental] = useState('');
  const [procesoId, setProcesoId] = useState('');
  const [edicion, setEdicion] = useState(1);

  // Result
  const [importResult, setImportResult] = useState<{ codigo: string; documentoId: string } | null>(
    null,
  );

  // ─── Drag & Drop ────────────────────────────────────────────────────────

  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleFileSelected = async (f: File) => {
    const ext = f.name.split('.').pop()?.toLowerCase();
    if (ext !== 'docx' && ext !== 'pdf') {
      setError('Solo se admiten archivos .docx y .pdf');
      return;
    }

    setFile(f);
    setError(null);
    setLoading(true);

    try {
      // Parse file and load processes in parallel
      const [parseRes, procesosRes] = await Promise.all([parsearDocumento(f), fetchProcesos()]);

      const result = parseRes.data as ParseResult;
      setParseResult(result);
      setProcesos((procesosRes.data ?? []) as ProcesoOption[]);

      // Pre-fill metadata from parsed result
      setCodigo(result.metadatos.codigo ?? '');
      setNombre(
        result.metadatos.nombre ?? result.metadatos.titulo ?? f.name.replace(/\.[^.]+$/, ''),
      );
      setTipoDocumental(result.metadatos.tipoDocumental ?? '');

      // Try to match process from code
      if (result.metadatos.codigo) {
        const processCode = result.metadatos.codigo.split('.').slice(0, 2).join('.');
        const match = (procesosRes.data ?? []).find((p: ProcesoOption) => p.codigo === processCode);
        if (match) setProcesoId(match.id);
      }

      setStep('preview');
    } catch (err: any) {
      setError(err.message ?? 'Error al parsear el archivo');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFileSelected(dropped);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) handleFileSelected(selected);
  }, []);

  // ─── Confirm import ─────────────────────────────────────────────────────

  const handleConfirm = async () => {
    if (!parseResult || !codigo || !nombre || !tipoDocumental) {
      setError('Complete todos los campos obligatorios');
      return;
    }

    setStep('confirming');
    setError(null);

    try {
      const res = await confirmarImportacion({
        metadatos: {
          codigo,
          nombre,
          tipoDocumental,
          procesoId: procesoId || undefined,
          direccion: 'CEO',
        },
        secciones: parseResult.secciones,
        edicion,
      });

      setImportResult(res.data);
      setStep('done');
    } catch (err: any) {
      setError(err.message ?? 'Error al confirmar la importacion');
      setStep('error');
    }
  };

  // ─── Section toggle ─────────────────────────────────────────────────────

  const toggleSection = (orden: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(orden)) next.delete(orden);
      else next.add(orden);
      return next;
    });
  };

  // ─── Reset ──────────────────────────────────────────────────────────────

  const handleReset = () => {
    setStep('upload');
    setFile(null);
    setParseResult(null);
    setError(null);
    setCodigo('');
    setNombre('');
    setTipoDocumental('');
    setProcesoId('');
    setEdicion(1);
    setImportResult(null);
    setExpandedSections(new Set());
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: '100vh', backgroundColor: C.bg }}>
      <AppHeader />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px' }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>
            {t.importar.titulo}
          </h1>
          <p style={{ fontSize: 14, color: C.textSec, marginTop: 4 }}>{t.importar.descripcion}</p>
        </div>

        {/* Step: Upload */}
        {step === 'upload' && (
          <div style={card}>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                padding: 60,
                textAlign: 'center',
                cursor: 'pointer',
                border: dragOver ? `2px dashed ${C.azul2}` : '2px dashed transparent',
                backgroundColor: dragOver ? `${C.azul2}10` : 'transparent',
                borderRadius: 10,
                transition: 'all 0.2s',
              }}
            >
              <div style={{ marginBottom: 16 }}>
                <Ic d={ICON_PATHS.download} s={48} c={C.azul2} />
              </div>
              <p style={{ fontSize: 16, fontWeight: 600, color: C.text, margin: 0 }}>
                {loading ? t.importar.procesando : t.importar.arrastrar}
              </p>
              <p style={{ fontSize: 13, color: C.textSec, marginTop: 8 }}>
                {t.importar.oSeleccionar}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx,.pdf"
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && parseResult && (
          <>
            {/* Warnings */}
            {parseResult.advertencias.length > 0 && (
              <div
                style={{
                  padding: '12px 16px',
                  marginBottom: 16,
                  backgroundColor: '#FEF3C7',
                  border: '1px solid #F59E0B',
                  borderRadius: 8,
                  fontSize: 13,
                  color: '#92400E',
                }}
              >
                <strong>Advertencias:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                  {parseResult.advertencias.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Metadata form */}
            <div style={{ ...card, padding: 24, marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: '0 0 16px' }}>
                {t.importar.metadatos}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={labelStyle}>Codigo *</div>
                  <input
                    style={inputStyle}
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                    placeholder={t.common.exampleCode}
                  />
                </div>
                <div>
                  <div style={labelStyle}>Nombre *</div>
                  <input
                    style={inputStyle}
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    placeholder={t.common.documentName}
                  />
                </div>
                <div>
                  <div style={labelStyle}>Tipo Documental *</div>
                  <select
                    style={selectStyle}
                    value={tipoDocumental}
                    onChange={(e) => setTipoDocumental(e.target.value)}
                  >
                    <option value="">Seleccione...</option>
                    {TIPO_DOCUMENTAL_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={labelStyle}>Proceso</div>
                  <select
                    style={selectStyle}
                    value={procesoId}
                    onChange={(e) => setProcesoId(e.target.value)}
                  >
                    <option value="">Auto-detectar desde codigo</option>
                    {procesos.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.codigo} — {p.nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={labelStyle}>Edicion</div>
                  <input
                    style={inputStyle}
                    type="number"
                    min={1}
                    value={edicion}
                    onChange={(e) => setEdicion(parseInt(e.target.value) || 1)}
                  />
                </div>
                <div>
                  <div style={labelStyle}>Archivo</div>
                  <div
                    style={{
                      ...inputStyle,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      color: C.textSec,
                    }}
                  >
                    <Ic d={ICON_PATHS.doc} s={16} c={C.textSec} />
                    {file?.name}
                  </div>
                </div>
              </div>
            </div>

            {/* Sections tree */}
            <div style={{ ...card, marginBottom: 16 }}>
              <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}` }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: 0 }}>
                  Estructura Detectada — {parseResult.secciones.length} secciones
                </h3>
              </div>
              <div style={{ maxHeight: 400, overflow: 'auto' }}>
                {parseResult.secciones.map((seccion) => (
                  <div key={seccion.orden}>
                    <div
                      onClick={() => toggleSection(seccion.orden)}
                      style={{
                        padding: '10px 24px',
                        paddingLeft: 24 + (seccion.nivel - 1) * 20,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        borderBottom: `1px solid ${C.border}`,
                        backgroundColor: expandedSections.has(seccion.orden)
                          ? `${C.azul2}08`
                          : 'transparent',
                        transition: 'background 0.15s',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          color: C.textSec,
                          transform: expandedSections.has(seccion.orden) ? 'rotate(90deg)' : 'none',
                          transition: 'transform 0.15s',
                        }}
                      >
                        {'\u25B6'}
                      </span>
                      <span
                        style={{
                          fontSize: seccion.nivel === 1 ? 14 : 13,
                          fontWeight: seccion.nivel <= 2 ? 600 : 400,
                          color: C.text,
                        }}
                      >
                        {seccion.titulo}
                      </span>
                      <span style={{ fontSize: 11, color: C.textSec, marginLeft: 'auto' }}>
                        {seccion.bloques.length} bloques
                      </span>
                    </div>
                    {expandedSections.has(seccion.orden) && (
                      <div
                        style={{
                          padding: '8px 24px 8px',
                          paddingLeft: 24 + seccion.nivel * 20,
                          backgroundColor: `${C.bg}`,
                          borderBottom: `1px solid ${C.border}`,
                        }}
                      >
                        {seccion.bloques.map((bloque, bi) => (
                          <div
                            key={bi}
                            style={{
                              fontSize: 12,
                              color: C.textSec,
                              padding: '4px 0',
                              display: 'flex',
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 10,
                                padding: '2px 6px',
                                borderRadius: 4,
                                backgroundColor: C.border,
                                color: C.text,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {bloque.tipo}
                            </span>
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {extractPreview(bloque.contenido)}
                            </span>
                          </div>
                        ))}
                        {seccion.bloques.length === 0 && (
                          <span style={{ fontSize: 12, color: C.textSec, fontStyle: 'italic' }}>
                            (seccion vacia)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button style={btnSecondary} onClick={handleReset}>
                {t.importar.cancelar}
              </button>
              <button
                style={{
                  ...btnPrimary,
                  opacity: !codigo || !nombre || !tipoDocumental ? 0.5 : 1,
                }}
                disabled={!codigo || !nombre || !tipoDocumental}
                onClick={handleConfirm}
              >
                <Ic d={ICON_PATHS.check} s={16} c="#fff" />
                {t.importar.confirmar}
              </button>
            </div>
          </>
        )}

        {/* Step: Confirming */}
        {step === 'confirming' && (
          <div style={{ ...card, padding: 48, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: C.text }}>
              {t.importar.importando}
            </div>
            <p style={{ fontSize: 13, color: C.textSec, marginTop: 8 }}>
              {t.importar.creandoDocumento}
            </p>
          </div>
        )}

        {/* Step: Done */}
        {step === 'done' && importResult && (
          <div style={{ ...card, padding: 48, textAlign: 'center' }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: '#D1FAE5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <Ic d={ICON_PATHS.check} s={24} c="#059669" />
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{t.importar.exito}</div>
            <p style={{ fontSize: 14, color: C.textSec, marginTop: 8 }}>
              Codigo: <strong>{importResult.codigo}</strong>
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24 }}>
              <button style={btnSecondary} onClick={handleReset}>
                {t.importar.importarOtro}
              </button>
              <button
                style={btnPrimary}
                onClick={() => navigate(`/documento/${importResult.codigo}`)}
              >
                {t.importar.verDocumento}
              </button>
            </div>
          </div>
        )}

        {/* Step: Error */}
        {(step === 'error' || error) && (
          <div
            style={{
              ...card,
              padding: 24,
              marginTop: step === 'error' ? 0 : 16,
              borderColor: '#FCA5A5',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Ic d={ICON_PATHS.x} s={20} c="#DC2626" />
              <span style={{ fontSize: 14, fontWeight: 600, color: '#DC2626' }}>Error</span>
            </div>
            <p style={{ fontSize: 13, color: C.text, marginTop: 8 }}>{error}</p>
            {step === 'error' && (
              <button style={{ ...btnSecondary, marginTop: 12 }} onClick={handleReset}>
                {t.importar.reintentar}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function extractPreview(contenido: any): string {
  if (!contenido) return '';
  if (typeof contenido === 'string') return contenido;

  // Walk ProseMirror JSON to extract text
  function walkText(node: any): string {
    if (node.text) return node.text;
    if (node.content) return node.content.map(walkText).join('');
    return '';
  }

  const text = walkText(contenido);
  return text.length > 80 ? text.slice(0, 80) + '...' : text;
}
